import app from 'flarum/forum/app';
import Stream from 'flarum/common/utils/Stream';

/**
 * Map plans on the Atlas page: a member's saved view with their own lines and notes.
 * Mixed into ResourceMapPage (methods use the page's map, filters, layers and style).
 */
const API = () => app.forum.attribute('apiUrl') + '/resource-map/plans';
export const LINE_TYPES = [['road', 'Road route', '#e8590c'], ['logistics', 'Logistics route', '#7b2cbf'], ['trade', 'Trade route', '#0b7285'], ['other', 'Other', '#495057']];
const VISIBILITY = [['private', 'Private: only me'], ['link', 'Anyone with the link'], ['public', 'Public: listed after review']];
const errText = (err, fallback) => err && err.response && err.response.errors && err.response.errors[0] && err.response.errors[0].detail || fallback;
const signIn = () => {
  try {
    const shown = app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
    if (shown && typeof shown.catch === 'function') shown.catch(() => {});
  } catch (e) {}
};
export const planMethods = {
  /** The Plans panel: mine, members' (approved), and waiting for review (editors) */
  openPlans() {
    this.selected = null;
    this.plan = null;
    this.plansList = 'loading';
    app.request({
      method: 'GET',
      url: API(),
      errorHandler: () => {}
    }).then(r => {
      this.plansList = r;
      m.redraw();
    }).catch(() => {
      this.plansList = null;
      m.redraw();
    });
  },
  /** Open a plan (from its address or a list): its view, style, layers, filters and drawing */
  openPlan(tokenOrPlan) {
    const fetched = typeof tokenOrPlan === 'object' ? Promise.resolve({
      plan: tokenOrPlan
    }) : app.request({
      method: 'GET',
      url: API() + '/' + tokenOrPlan,
      errorHandler: () => {}
    });
    return fetched.then(r => {
      const p = r.plan;
      this.plansList = null;
      this.selected = null;
      this.plan = p;
      const v = p.view || {};
      if (v.style && v.style !== this.style) this.setStyle(v.style);
      Object.entries(v.layers || {}).forEach(_ref => {
        let [k, on] = _ref;
        if (this.layers[k] !== on) {
          this.layers[k] = on;
          this.map && this.map.setLayer(k, on);
        }
      });
      Object.assign(this.f, {
        query: '',
        category: '',
        crop: '',
        element: '',
        country: ''
      }, v.filters || {});
      if (v.ecowasOnly !== false !== this.ecowasOnly) this.setEcowasOnly(v.ecowasOnly !== false);
      this.refresh(false);
      this.southUp = Math.abs(Math.abs(v.bearing || 0) - 180) < 1;
      if (this.map) {
        this.map.setPlan(p.features);
        setTimeout(() => this.map && this.map.setView(v), 250);
      }
      if (!this.embed) {
        try {
          window.history.replaceState(window.history.state, '', app.route('voucha-resourcemap.plan', {
            plan: p.path
          }));
        } catch (e) {}
      }
      app.setTitle(p.title + ' · Voucha Atlas');
      m.redraw();
    }).catch(err => {
      window.alert(errText(err, 'That plan could not be opened.'));
    });
  },
  closePlan() {
    this.plan = null;
    if (this.map) this.map.setPlan(null);
    try {
      window.history.replaceState(window.history.state, '', app.route('voucha-resourcemap'));
    } catch (e) {}
  },
  /** Start a new plan, or edit one */
  startPlan(p) {
    if (p === void 0) {
      p = null;
    }
    if (!app.session.user) {
      try {
        window.sessionStorage.setItem('voucha-rm-plan', '1');
      } catch (e) {}
      return signIn();
    }
    this.plansList = null;
    this.selected = null;
    this.planEdit = {
      token: p ? p.token : null,
      title: p ? p.title : '',
      description: p ? p.description || '' : '',
      visibility: p ? p.visibility : 'link',
      features: p ? JSON.parse(JSON.stringify(p.features)) : {
        lines: [],
        notes: []
      },
      lineType: 'road',
      drawing: null,
      noting: null,
      error: null,
      saving: false
    };
    this.plan = null;
    if (this.map) this.map.setPlan(this.planEdit.features);
  },
  /** Drawing a line: click along the route, then Finish */
  drawLine() {
    const e = this.planEdit;
    this.stopNote();
    e.drawing = {
      type: e.lineType,
      coords: []
    };
    this.map.startPick('line', pts => {
      e.drawing.coords = pts;
      m.redraw();
    });
  },
  finishLine() {
    const e = this.planEdit;
    if (e.drawing && e.drawing.coords.length >= 2) e.features.lines.push({
      type: e.drawing.type,
      label: '',
      coords: e.drawing.coords
    });
    e.drawing = null;
    this.map.stopPick();
    this.map.setPlan(e.features);
  },
  /** Adding a note: click the spot, then type */
  addNote() {
    const e = this.planEdit;
    if (e.drawing) this.finishLine();
    e.noting = {
      lat: null,
      lng: null,
      text: ''
    };
    this.map.startPick('point', pt => {
      e.noting.lat = pt.lat;
      e.noting.lng = pt.lng;
      m.redraw();
    });
  },
  saveNote() {
    const e = this.planEdit;
    if (e.noting && e.noting.lat != null && e.noting.text.trim()) e.features.notes.push({
      lat: e.noting.lat,
      lng: e.noting.lng,
      text: e.noting.text.trim().slice(0, 280)
    });
    this.stopNote();
    this.map.setPlan(e.features);
  },
  stopNote() {
    if (this.planEdit && this.planEdit.noting) {
      this.planEdit.noting = null;
      this.map && this.map.stopPick();
    }
  },
  removeFeature(kind, i) {
    this.planEdit.features[kind].splice(i, 1);
    this.map.setPlan(this.planEdit.features);
  },
  cancelPlan() {
    if (this.map) {
      this.map.stopPick();
      this.map.setPlan(null);
    }
    this.planEdit = null;
  },
  /** Save: the view and drawing, then the map image, then open the plan at its address */
  savePlan() {
    const e = this.planEdit;
    if (e.drawing) this.finishLine();
    this.stopNote();
    e.saving = true;
    e.error = null;
    const body = {
      title: e.title,
      description: e.description,
      visibility: e.visibility,
      features: e.features,
      view: {
        ...this.map.getView(),
        style: this.style,
        layers: {
          ...this.layers
        },
        filters: {
          ...this.f
        },
        ecowasOnly: this.ecowasOnly
      }
    };
    app.request({
      method: e.token ? 'PATCH' : 'POST',
      url: API() + (e.token ? '/' + e.token : ''),
      body,
      errorHandler: () => {}
    }).then(r => {
      // The plan is saved: open it straight away (the map already shows it as saved)...
      const saved = r.plan;
      this.planEdit = null;
      this.map.stopPick();
      this.imageStatus = 'saving';
      this.openPlan(saved);
      // ...and capture and upload its map image in the background (for link previews)
      this.map.capture().then(image => app.request({
        method: 'POST',
        url: API() + '/' + saved.token + '/image',
        body: {
          image
        },
        errorHandler: () => {}
      })).then(res => {
        if (this.plan && this.plan.token === saved.token) this.plan.image = res.image;
        this.imageStatus = null;
      }).catch(err => {
        console.warn('[Voucha Atlas] plan image not saved:', err);
        this.imageStatus = 'failed';
      }).then(() => m.redraw());
    }).catch(err => {
      e.saving = false;
      e.error = errText(err, 'The plan could not be saved. Please try again.');
      m.redraw();
    });
  },
  deletePlan(p) {
    if (!window.confirm('Delete "' + p.title + '"? This cannot be undone.')) return;
    app.request({
      method: 'DELETE',
      url: API() + '/' + p.token,
      errorHandler: () => {}
    }).then(() => {
      this.closePlan();
      this.openPlans();
    });
  },
  reviewPlan(p, approve) {
    app.request({
      method: 'POST',
      url: API() + '/' + p.token + '/review',
      body: {
        decision: approve ? 'approve' : 'reject'
      },
      errorHandler: () => {}
    }).then(() => this.openPlans());
  },
  sharePlan(p) {
    const url = window.location.origin + app.route('voucha-resourcemap.plan', {
      plan: p.path
    });
    if (navigator.share) return navigator.share({
      title: p.title + ' · Voucha Atlas',
      text: p.title,
      url
    }).catch(() => {});
    (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject()).then(() => {
      this.copied = 'plan:' + p.token;
      m.redraw();
      setTimeout(() => {
        this.copied = null;
        m.redraw();
      }, 2500);
    }).catch(() => window.prompt('Copy this link:', url));
  },
  /** Post as a discussion: the member's own composer, pre-filled (image, description, link), default tag selected */
  discussPlan(p) {
    if (p.discussionId) return m.route.set('/d/' + p.discussionId);
    if (!app.session.user) return signIn();
    app.request({
      method: 'GET',
      url: API() + '/' + p.token + '/draft',
      errorHandler: () => {}
    }).then(draft => {
      const wanted = String(draft.tag || '').toLowerCase();
      const tag = (app.store.all('tags') || []).find(t => String(t.name() || '').toLowerCase() === wanted || String(t.slug() || '').toLowerCase() === wanted);
      return app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/DiscussionComposer'), {
        user: app.session.user,
        originalContent: draft.content
      }).then(loaded => {
        if (!loaded) return;
        app.composer.fields.title = Stream(draft.title);
        app.composer.fields.tags = tag ? [tag] : [];
        app.composer.show();
      });
    }).catch(err => {
      console.error('[Voucha Atlas] posting the plan failed:', err);
      window.alert(errText(err, 'The discussion could not be started (' + (err && err.message || 'unknown error') + ').'));
    });
  },
  // ---------- panels ----------
  plansPanel() {
    const L = this.plansList;
    if (L === 'loading') return m("div", {
      className: "RMPanel is-empty"
    }, "Loading plans\u2026");
    const item = (p, extra) => m("li", null, m("button", {
      type: "button",
      className: "RMLinkish",
      onclick: () => this.openPlan(p.token)
    }, p.title), m("span", {
      className: "RMMuted"
    }, " \xB7 by ", p.by || 'a member', p.visibility === 'private' ? ' · private' : p.visibility === 'link' ? ' · link only' : p.review === 'pending' ? ' · waiting for review' : ''), extra || null);
    return m("section", {
      className: "RMPanel",
      "aria-label": "Map plans"
    }, m("div", {
      className: "RMPanel-head"
    }, m("div", null, m("h2", null, "Map plans"), m("p", {
      className: "RMMuted"
    }, "Members' own maps: routes, corridors and notes. Plans are members' ideas, not official routes.")), m("div", {
      className: "RMPanel-actions"
    }, m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => this.startPlan()
    }, m("i", {
      className: "fas fa-plus"
    }), " New plan"), m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.plansList = null
    }, "Close"))), L.pending && L.pending.length ? m("div", {
      className: "RMPlans-group"
    }, m("h3", null, "Waiting for review (", L.pending.length, ")"), m("ul", {
      className: "RMList"
    }, L.pending.map(p => item(p, m("span", {
      className: "RMPlans-review"
    }, m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => this.reviewPlan(p, true)
    }, "Approve"), m("button", {
      type: "button",
      className: "Button RMDanger",
      onclick: () => this.reviewPlan(p, false)
    }, "Reject")))))) : null, app.session.user ? m("div", {
      className: "RMPlans-group"
    }, m("h3", null, "Your plans"), L.mine && L.mine.length ? m("ul", {
      className: "RMList"
    }, L.mine.map(p => item(p))) : m("p", {
      className: "RMMuted"
    }, "None yet. Start one with \"New plan\".")) : null, m("div", {
      className: "RMPlans-group"
    }, m("h3", null, "Members' plans"), L.public && L.public.length ? m("ul", {
      className: "RMList"
    }, L.public.map(p => item(p))) : m("p", {
      className: "RMMuted"
    }, "No public plans yet.")));
  },
  planView() {
    const p = this.plan;
    const n = (p.features.lines || []).length;
    const k = (p.features.notes || []).length;
    return m("section", {
      className: "RMPanel",
      "aria-label": p.title
    }, m("div", {
      className: "RMPanel-head"
    }, m("div", null, m("h2", null, p.title), m("p", {
      className: "RMMuted"
    }, "Map plan by ", p.by || 'a member', " \xB7 ", n, " route", n === 1 ? '' : 's', ", ", k, " note", k === 1 ? '' : 's', p.visibility === 'private' ? ' · private' : p.visibility === 'link' ? ' · link only' : p.review === 'pending' ? ' · waiting for review' : '')), m("div", {
      className: "RMPanel-actions"
    }, p.canChange ? m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.startPlan(p)
    }, m("i", {
      className: "fas fa-pen"
    }), " Edit") : null, p.canChange ? m("button", {
      type: "button",
      className: "Button RMDanger",
      onclick: () => this.deletePlan(p)
    }, m("i", {
      className: "fas fa-trash"
    }), " Delete") : null, m("button", {
      type: "button",
      className: "Button",
      onclick: () => {
        this.closePlan();
      }
    }, "Close"))), m("p", {
      className: "RMPlanNotice"
    }, m("i", {
      className: "fas fa-info-circle"
    }), " A member's plan, not an official route or project."), this.imageStatus === 'saving' ? m("p", {
      className: "RMMuted RMSmall"
    }, m("i", {
      className: "fas fa-spinner fa-spin"
    }), " Saving the map image for link previews\u2026") : null, this.imageStatus === 'failed' ? m("p", {
      className: "RMMuted RMSmall"
    }, "The map image couldn't be saved (your plan is saved). Edit and save again to retry.") : null, p.description ? m("p", null, p.description) : null, m("div", {
      className: "RMPlanKey"
    }, LINE_TYPES.filter(_ref2 => {
      let [t] = _ref2;
      return (p.features.lines || []).some(l => l.type === t);
    }).map(_ref3 => {
      let [t, label, color] = _ref3;
      return m("span", null, m("i", {
        style: {
          background: color
        }
      }), " ", label);
    })), m("div", {
      className: "RMPanel-actions"
    }, p.visibility !== 'private' ? m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.sharePlan(p)
    }, m("i", {
      className: "fas fa-share-alt"
    }), " ", this.copied === 'plan:' + p.token ? 'Link copied' : 'Share') : null, p.visibility !== 'private' ? m("button", {
      type: "button",
      className: "Button Button--primary",
      onclick: () => this.discussPlan(p)
    }, m("i", {
      className: "fas fa-comments"
    }), " ", p.discussionId ? 'Discuss' : 'Post as a discussion') : m("span", {
      className: "RMMuted"
    }, "Private plans can't be shared. Edit it to share by link or publicly.")));
  },
  planEditor() {
    const e = this.planEdit;
    const f = e.features;
    return m("section", {
      className: "RMPanel RMEditor",
      "aria-label": "Map plan"
    }, m("div", {
      className: "RMPanel-head"
    }, m("h2", null, e.token ? 'Edit your plan' : 'New map plan')), m("p", {
      className: "RMMuted"
    }, "Set up the map as you want it (style, layers, filters, area), draw your routes and add notes. The plan saves the map exactly as you leave it."), m("div", {
      className: "RMFields"
    }, m("label", {
      className: "RMField"
    }, m("span", null, "Title"), m("input", {
      type: "text",
      maxlength: "120",
      value: e.title,
      placeholder: "e.g. Lagos\u2013Abidjan trucking corridor",
      oninput: ev => e.title = ev.target.value
    })), m("label", {
      className: "RMField"
    }, m("span", null, "Who can see it"), m("select", {
      value: e.visibility,
      onchange: ev => e.visibility = ev.target.value
    }, VISIBILITY.map(_ref4 => {
      let [v, t] = _ref4;
      return m("option", {
        value: v
      }, t);
    })))), m("label", {
      className: "RMField"
    }, m("span", null, "Description (", e.description.length, " / 1,000)"), m("textarea", {
      rows: "3",
      maxlength: "1000",
      value: e.description,
      oninput: ev => e.description = ev.target.value
    })), m("div", {
      className: "RMPlanTools"
    }, e.drawing ? m("span", null, m("i", {
      className: "fas fa-route"
    }), " Drawing a ", (LINE_TYPES.find(_ref5 => {
      let [t] = _ref5;
      return t === e.drawing.type;
    }) || [])[1], ": ", e.drawing.coords.length, " points.", ' ', m("button", {
      type: "button",
      className: "RMLinkish",
      onclick: () => this.map.undoPoint()
    }, "Undo point"), ' ', m("button", {
      type: "button",
      className: "Button Button--primary",
      disabled: e.drawing.coords.length < 2,
      onclick: () => this.finishLine()
    }, "Finish line")) : e.noting ? m("span", null, m("i", {
      className: "fas fa-map-pin"
    }), " ", e.noting.lat == null ? 'Click the map where the note goes.' : 'Note: ', e.noting.lat != null ? m("input", {
      type: "text",
      maxlength: "280",
      placeholder: "What's here?",
      value: e.noting.text,
      oninput: ev => e.noting.text = ev.target.value
    }) : null, e.noting.lat != null ? m("button", {
      type: "button",
      className: "Button Button--primary",
      disabled: !e.noting.text.trim(),
      onclick: () => this.saveNote()
    }, "Add note") : null, m("button", {
      type: "button",
      className: "RMLinkish",
      onclick: () => this.stopNote()
    }, "Cancel")) : m("span", null, m("select", {
      value: e.lineType,
      onchange: ev => e.lineType = ev.target.value,
      "aria-label": "Line type"
    }, LINE_TYPES.map(_ref6 => {
      let [t, label] = _ref6;
      return m("option", {
        value: t
      }, label);
    })), ' ', m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.drawLine(),
      disabled: f.lines.length >= 30
    }, m("i", {
      className: "fas fa-pencil-alt"
    }), " Draw a line"), ' ', m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.addNote(),
      disabled: f.notes.length >= 50
    }, m("i", {
      className: "fas fa-sticky-note"
    }), " Add a note"))), f.lines.length || f.notes.length ? m("ul", {
      className: "RMList RMPlanList"
    }, f.lines.map((l, i) => m("li", null, m("i", {
      style: {
        color: (LINE_TYPES.find(_ref7 => {
          let [t] = _ref7;
          return t === l.type;
        }) || [])[2]
      },
      className: "fas fa-route"
    }), " ", (LINE_TYPES.find(_ref8 => {
      let [t] = _ref8;
      return t === l.type;
    }) || [])[1], " \xB7 ", l.coords.length, " points ", m("button", {
      type: "button",
      className: "RMLinkish",
      onclick: () => this.removeFeature('lines', i)
    }, "Remove"))), f.notes.map((n, i) => m("li", null, m("i", {
      className: "fas fa-sticky-note"
    }), " ", n.text, " ", m("button", {
      type: "button",
      className: "RMLinkish",
      onclick: () => this.removeFeature('notes', i)
    }, "Remove")))) : m("p", {
      className: "RMMuted"
    }, "Nothing drawn yet."), e.error ? m("p", {
      className: "RMEditor-error",
      role: "alert"
    }, e.error) : null, m("div", {
      className: "RMEditor-actions"
    }, m("button", {
      type: "button",
      className: "Button Button--primary",
      disabled: e.saving,
      onclick: () => this.savePlan()
    }, e.saving ? 'Saving…' : 'Save plan'), m("button", {
      type: "button",
      className: "Button",
      onclick: () => this.cancelPlan()
    }, "Cancel")));
  }
};